// src/components/activities/CoachingSessionPage.jsx

import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext'; // Ajusta la ruta si es necesario
import { CheckBadgeIcon, RocketLaunchIcon, KeyIcon, ArrowRightIcon, CalendarDaysIcon, LightBulbIcon, ShieldCheckIcon } from '@heroicons/react/24/solid';

const PREMIUM_ROLE_ID = 7;

// --- VISTA PARA USUARIOS PREMIUM (YA PAGARON) ---
// NOTA: También se actualizó el texto aquí a "Sesión de Clarificación" por consistencia.
const PremiumUserView = ({ user }) => (
    <div className="max-w-3xl mx-auto text-center py-12">
        <CalendarDaysIcon className="w-20 h-20 text-green-400 mx-auto mb-6" />
        <h1 className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-green-300 via-green-400 to-green-500">
            ¡Bienvenido a tu Sesión de Clarificación, {user ? user.username : ''}!
        </h1>
        <p className="mt-6 text-lg text-gray-300">
            Gracias por dar el paso y comprometerte con tu transformación. Esta sesión 1 a 1 de 30 minutos está diseñada para crear un plan de acción personalizado basado en todo lo que has descubierto hasta ahora.
        </p>
        <div className="mt-10">
            {/* 
              👇 ¡ACCIÓN REQUERIDA! 
              Este es el enlace para usuarios que YA PAGARON. Reemplázalo si es diferente al de la llamada gratuita.
            */}
            <a 
                href="https://calendly.com/tu-usuario/sesion-premium"
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 bg-green-500 text-black font-bold py-4 px-12 rounded-lg text-xl hover:bg-green-400 transition-transform transform hover:scale-105"
            >
                Agendar mi Sesión Ahora
                <ArrowRightIcon className="w-6 h-6" />
            </a>
        </div>
        <p className="mt-4 text-sm text-gray-500">
            Serás redirigido a Calendly para elegir el horario que mejor te convenga.
        </p>
    </div>
);


// --- VISTA PARA USUARIOS GRATUITOS (LA PÁGINA DE VENTAS) ---
const FreeUserView = ({ user }) => (
    <div className="max-w-4xl mx-auto text-white">
        {/* FASE 1: RECONOCIMIENTO Y VALIDACIÓN */}
        <header className="text-center p-8 bg-gray-900 rounded-xl border border-yellow-500/20 shadow-lg">
            <h1 className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-yellow-200 via-yellow-400 to-yellow-600">
                ¡Felicidades, {user ? user.username : 'viajero'}!
            </h1>
            <p className="mt-4 text-lg text-gray-300">
                Has completado la fase más profunda y reveladora. El trabajo que acabas de hacer te pone en el 1% de las personas que realmente se comprometen con su propio crecimiento.
            </p>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
                <div className="bg-gray-800 p-4 rounded-lg flex items-center gap-4"><CheckBadgeIcon className="w-10 h-10 text-green-400 flex-shrink-0"/><div><h3 className="font-bold">Diagnóstico Claro</h3><p className="text-sm text-gray-400">Sabes exactamente dónde estás.</p></div></div>
                <div className="bg-gray-800 p-4 rounded-lg flex items-center gap-4"><RocketLaunchIcon className="w-10 h-10 text-blue-400 flex-shrink-0"/><div><h3 className="font-bold">Visión Emocionante</h3><p className="text-sm text-gray-400">Sabes exactamente a dónde vas.</p></div></div>
                <div className="bg-gray-800 p-4 rounded-lg flex items-center gap-4"><KeyIcon className="w-10 h-10 text-purple-400 flex-shrink-0"/><div><h3 className="font-bold">Motor Inquebrantable</h3><p className="text-sm text-gray-400">Sabes EXACTAMENTE por qué vas.</p></div></div>
            </div>
        </header>

        {/* FASE 2: PRESENTAR LA BRECHA */}
        <section className="my-16 text-center">
            <h2 className="text-3xl font-bold">¿Y Ahora Qué? El Puente Hacia Tu Nueva Realidad</h2>
            <p className="mt-4 text-gray-400 max-w-2xl mx-auto">Tienes el mapa (tu visión) y el combustible (tu porqué). La pregunta es: <span className="text-yellow-400 font-bold">¿cómo construimos el vehículo que te lleve allí de forma rápida y segura?</span></p>
        </section>

        {/* FASE 3: LA SOLUCIÓN */}
        <section className="p-8 bg-gray-900/70 rounded-xl border border-purple-500/30">
            <h2 className="text-3xl font-bold text-center mb-8 text-transparent bg-clip-text bg-gradient-to-r from-purple-300 to-yellow-300">La Solución: Una Sesión de Clarificación 1 a 1</h2>
            <p className="text-center text-gray-400 mb-8 max-w-2xl mx-auto">
                La mejor forma de construir ese puente es juntos. En esta sesión personalizada, nos enfocaremos 100% en ti para:
            </p>
            <div className="space-y-6">
                <div className="flex items-start gap-4"><LightBulbIcon className="w-8 h-8 text-yellow-400 flex-shrink-0 mt-1"/><div><h3 className="font-bold text-xl">Obtener Claridad Absoluta</h3><p className="text-gray-400">Conectaremos tu visión con tu situación actual para trazar la ruta más directa. Identificaremos el obstáculo #1 que te está frenando ahora mismo.</p></div></div>
                <div className="flex items-start gap-4"><ArrowRightIcon className="w-8 h-8 text-yellow-400 flex-shrink-0 mt-1"/><div><h3 className="font-bold text-xl">Crear un Plan de Acción Inmediato</h3><p className="text-gray-400">Saldrás de la llamada sabiendo exactamente cuáles son los 2 o 3 siguientes pasos que debes dar. No más dudas, solo acción enfocada.</p></div></div>
                <div className="flex items-start gap-4"><ShieldCheckIcon className="w-8 h-8 text-yellow-400 flex-shrink-0 mt-1"/><div><h3 className="font-bold text-xl">Validar si Somos un Buen Equipo</h3><p className="text-gray-400">Veremos si mi método de coaching es el adecuado para ti. Esta llamada es tanto para ti como para mí, para asegurar que una futura colaboración sea exitosa.</p></div></div>
            </div>
        </section>

        {/* FASE 4: LA OFERTA Y CTA */}
        <section className="my-16 p-8 rounded-2xl bg-gradient-to-br from-purple-900/50 via-gray-900 to-gray-900 border border-purple-500/50 text-center">
            <h2 className="text-3xl font-bold">Es Hora de Tomar la Decisión</h2>
            <p className="mt-4 text-gray-300 max-w-3xl mx-auto">
                Este es el portal hacia la claridad. Da el siguiente paso y comprométete con tu transformación agendando tu sesión de clarificación gratuita.
            </p>
            
            <div className="mt-10 flex flex-col items-center gap-4">
                <a 
                    href="https://calendly.com/gustavonahuelaguiar/30min" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-full md:w-auto inline-flex items-center justify-center gap-3 bg-purple-500 text-white font-bold py-4 px-12 rounded-lg text-xl hover:bg-purple-400 transition-transform transform hover:scale-105"
                >
                    <CalendarDaysIcon className="w-6 h-6" />
                    Agendar mi Sesión de Clarificación Gratis
                </a>
                 <p className="mt-2 text-sm text-gray-400">
                    Sin costo y sin compromiso. El único objetivo es darte claridad sobre tu camino.
                </p>
            </div>
        </section>
    </div>
);


// --- COMPONENTE PRINCIPAL ORQUESTADOR ---
const CoachingSessionPage = () => {
    const { user } = useAuth();
    
    // Comprueba si el usuario tiene el rol premium. 
    const isPremium = user?.roles?.includes(PREMIUM_ROLE_ID);

    // Si aún no se ha cargado la información del usuario, mostramos un loader
    if (user === undefined) {
        return <p className="text-center text-yellow-400 animate-pulse">Verificando tu acceso...</p>;
    }

    return isPremium ? <PremiumUserView user={user} /> : <FreeUserView user={user} />;
};

export default CoachingSessionPage;